import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

import { callLogsKeys } from "./use-call-logs";

export interface Campaign {
	id: string;
	title: string;
	enabled: boolean;
	timezone: string;
	status: string;
	group: {
		id: string;
		name: string;
		filter: string[];
		isStatic: boolean;
	};
	agent: {
		id: string;
		name: string;
	};
	phoneNumbers: Array<{
		id: string;
		number: string;
	}>;
	startAt: string;
	endAt: string;
	createdAt?: string;
	leadsCount: number;
	callsCount: number;
	duration: number;
	settings: Record<string, any>;
}

export interface Lead {
	id: string;
	name: string;
	phoneNumber: string;
	email: string;
}
export interface Disposition {
	id: string;
	name: string;
	label: string;
	description: string;
	usedForAnalyses: boolean;
	sendEmail: boolean;
	isSystem: boolean;
	countsAsConversion: boolean;
}

export interface DispositionStatistic {
	name: string;
	label: string;
	count: number;
	countsAsConversion: boolean;
}

export interface CampaignListHealth {
	contactsInList: number;
	uniqueContactsDialed: number;
	uniqueContactsConnected: number;
	undialedContacts: number;
	listPenetrationRate: number;
}

export interface CampaignRates {
	connectionRate: number;
	conversionRate: number;
	leadCompletionRate: number;
}

export interface CampaignTotals {
	totalOutboundDials: number;
	connectedCalls: number;
	conversionsCount: number;
}

export interface CampaignInfo {
	id: string;
	title: string;
	status: string;
	timezone: string;
	contactsCount: number;
	leads: { done: number; total: number };
	callsCount: number;
	duration: number;
	aiMinutes: number;
	dispositions: DispositionStatistic[];
	listHealth: CampaignListHealth;
	rates: CampaignRates;
	totals: CampaignTotals;
	conversionDispositions: string[];
}

export type TimeseriesMetric = "conversions" | "penetration";
export type TimeseriesGranularity = "day" | "hour";

export interface TimeseriesPoint {
	period: string;
	value: number;
}

export interface CustomField {
	id: string;
	campaignId: string;
	name: string;
	label: string;
	description: string;
	enabled: boolean;
}

export enum LeadUpdateAction {
	ADD_TO_QUEUE = "add_to_queue",
	SCHEDULE_CUSTOM_CALL_BACK = "schedule_custom_call_back",
	RESET_CALL_BACK_ATTEMPTS = "reset_call_back_attempts",
	DO_NOT_CALL_AGAIN = "do_not_call_again",
	ANALYSE = "analyse",
}

export const campaignsKeys = {
	all: ["campaigns"] as const,
	lists: () => [...campaignsKeys.all, "list"] as const,
	list: (query?: string) => [...campaignsKeys.lists(), { query }] as const,
	details: () => [...campaignsKeys.all, "detail"] as const,
	detail: (id: string) => [...campaignsKeys.details(), id] as const,
	infoBase: () => [...campaignsKeys.all, "info"] as const,
	info: (id: string) => [...campaignsKeys.infoBase(), id] as const,
	stats: () => [...campaignsKeys.all, "stats"] as const,
	leads: (campaignId?: string) => [...campaignsKeys.all, "leads", ...(campaignId ? [campaignId] : [])] as const,
	lead: (campaignId: string, id: string) => [...campaignsKeys.leads(campaignId), id] as const,
	calls: (campaignId?: string) => [...campaignsKeys.all, "calls", ...(campaignId ? [campaignId] : [])] as const,
	call: (campaignId: string, id: string) => [...campaignsKeys.calls(campaignId), id] as const,
	dispositions: (campaignId?: string) =>
		[...campaignsKeys.all, "dispositions", ...(campaignId ? [campaignId] : [])] as const,
	disposition: (campaignId: string, id: string) => [...campaignsKeys.dispositions(campaignId), id] as const,
	fields: () => [...campaignsKeys.all, "fields"] as const,
	customFields: (campaignId?: string) =>
		[...campaignsKeys.all, "custom-fields", ...(campaignId ? [campaignId] : [])] as const,
	customField: (campaignId: string, id: string) => [...campaignsKeys.customFields(campaignId), id] as const,
	summarySchema: (campaignId?: string) =>
		[...campaignsKeys.all, "summary-schema", ...(campaignId ? [campaignId] : [])] as const,
	timeseries: (campaignId: string, metric: TimeseriesMetric, granularity: TimeseriesGranularity) =>
		[...campaignsKeys.info(campaignId), "timeseries", metric, granularity] as const,
};

export function useCampaignsList(query?: string) {
	return useQuery({
		queryKey: campaignsKeys.list(query),
		queryFn: async () => {
			const queryString = query ? `?${query}` : "";
			const response = await apiClient.get(`/campaigns${queryString}`);
			return response.data;
		},
	});
}

export function useCampaign(id: string) {
	return useQuery({
		queryKey: campaignsKeys.detail(id),
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useCampaignFields(campaignId: string) {
	return useQuery({
		queryKey: [...campaignsKeys.fields(), campaignId],
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/${campaignId}/settings-fields`);
			return response.data;
		},
		enabled: !!campaignId,
	});
}

export function useCampaignStats() {
	return useQuery({
		queryKey: campaignsKeys.stats(),
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/statistics`);
			return response.data;
		},
		refetchOnMount: "always",
	});
}

export function useCampaignInfo(id: string) {
	return useQuery({
		queryKey: campaignsKeys.info(id),
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/${id}/info`);
			return response.data;
		},
		refetchOnMount: "always",
		enabled: !!id,
	});
}

export function useCampaignTimeseries(
	id: string,
	metric: TimeseriesMetric,
	granularity: TimeseriesGranularity,
	enabled = true
) {
	return useQuery<TimeseriesPoint[]>({
		queryKey: campaignsKeys.timeseries(id, metric, granularity),
		queryFn: async () => {
			const response = await apiClient.get(
				`/campaigns/${id}/timeseries?metric=${metric}&granularity=${granularity}`
			);
			return response.data?.data ?? response.data ?? [];
		},
		enabled: !!id && enabled,
	});
}

export function useCreateCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: {
			title: string;
			enabled?: boolean;
			groupId?: string;
			agentId?: string;
			phoneNumbers?: string[];
			timezone?: string;
			startAt?: string;
			endAt?: string;
			scheduling?: {
				dow: number;
				from: string;
				to: string;
			}[];
			callBacks?: {
				phoneNumber: string;
				time: string;
			}[];
		}) => {
			const response = await apiClient.post("/campaigns", data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			toast.success("Campaign created successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Campaign creation failed");
		},
	});
}

export function useUpdateCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			data,
		}: {
			id: string;
			data: {
				title: string;
				enabled: boolean;
				groupId: string;
				agentId: string;
				phoneNumbers: string[];
			};
		}) => {
			const response = await apiClient.put(`/campaigns/${id}`, data);
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(campaignsKeys.detail(variables.id), data);
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.removeQueries({ queryKey: callLogsKeys.all });
			toast.success("Campaign updated successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Campaign update failed");
		},
	});
}

export function useDeleteCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const response = await apiClient.delete(`/campaigns/${id}`);
			return response.data;
		},
		onSuccess: (_, id) => {
			queryClient.removeQueries({ queryKey: campaignsKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.removeQueries({ queryKey: callLogsKeys.all });
			toast.success("Campaign deleted successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Campaign deletion failed");
		},
	});
}

export function useToggleCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
			const response = await apiClient.put(`/campaigns/${id}`, { enabled });
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(campaignsKeys.detail(variables.id), data);
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.stats() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.info(variables.id) });

			toast.success(`Campaign ${data.enabled ? "started" : "paused"} successfully`);
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Campaign update failed");
		},
	});
}

export function useDisposition(campaignId: string, id: string) {
	return useQuery({
		queryKey: campaignsKeys.disposition(campaignId, id),
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/${campaignId}/dispositions/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useCreateDisposition() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, data }: { campaignId: string; data: any }) => {
			const response = await apiClient.post(`/campaigns/${campaignId}/dispositions`, data);
			return response.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.dispositions(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
			toast.success("Disposition created successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Disposition creation failed");
		},
	});
}

export function useUpdateDisposition() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, id, data }: { campaignId: string; id: string; data: any }) => {
			const response = await apiClient.put(`/campaigns/${campaignId}/dispositions/${id}`, data);
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(campaignsKeys.disposition(variables.campaignId, variables.id), data);
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.dispositions(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
			toast.success("Disposition updated successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Disposition update failed");
		},
	});
}

export function useDeleteDisposition() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, id }: { campaignId: string; id: string }) => {
			const response = await apiClient.delete(`/campaigns/${campaignId}/dispositions/${id}`);
			return response.data;
		},
		onSuccess: (_, variables) => {
			queryClient.removeQueries({ queryKey: campaignsKeys.disposition(variables.campaignId, variables.id) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.dispositions(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
			toast.success("Disposition deleted successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Disposition deletion failed");
		},
	});
}

export function useCustomField(campaignId: string, id: string) {
	return useQuery({
		queryKey: campaignsKeys.customField(campaignId, id),
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/${campaignId}/fields/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useCreateCustomField() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, data }: { campaignId: string; data: any }) => {
			const response = await apiClient.post(`/campaigns/${campaignId}/fields`, data);
			return response.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignsKeys.customFields(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.customFields(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
			toast.success("Custom field created successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Custom field creation failed");
		},
	});
}

export function useUpdateCustomField() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, id, data }: { campaignId: string; id: string; data: any }) => {
			const response = await apiClient.put(`/campaigns/${campaignId}/fields/${id}`, data);
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(campaignsKeys.customField(variables.campaignId, variables.id), data);
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.customFields(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
			toast.success("Custom field updated successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Custom field update failed");
		},
	});
}

export function useDisableCustomField() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, id }: { campaignId: string; id: string }) => {
			const response = await apiClient.put(`/campaigns/${campaignId}/fields/${id}`, { enabled: false });
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(campaignsKeys.customField(variables.campaignId, variables.id), data);
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.customFields(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
			toast.success("Custom field disabled successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Custom field disable failed");
		},
	});
}

export function useDeleteCustomField() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, id }: { campaignId: string; id: string }) => {
			const response = await apiClient.delete(`/campaigns/${campaignId}/fields/${id}`);
			return response.data;
		},
		onSuccess: (_, variables) => {
			queryClient.removeQueries({ queryKey: campaignsKeys.customField(variables.campaignId, variables.id) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.customFields(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
			toast.success("Custom field deleted successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Custom field deletion failed");
		},
	});
}

export function useCampaignSummarySchema(id: string) {
	return useQuery({
		queryKey: campaignsKeys.summarySchema(id),
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/${id}/summary-schema`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useCampaignLead(campaignId: string, id: string) {
	return useQuery({
		queryKey: campaignsKeys.lead(campaignId, id),
		queryFn: async () => {
			const response = await apiClient.get(`/campaigns/${campaignId}/leads/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useUpdateCampaignLead() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			campaignId,
			status,
			disposition,
			attemptsFrom,
			attemptsTo,
			data,
			action,
		}: {
			campaignId: string;
			status?: string;
			disposition?: string[];
			attemptsFrom?: string;
			attemptsTo?: string;
			data: any;
			id?: string;
			action: string;
		}) => {
			const params: string[] = [];
			if (status) {
				params.push(`status=${status}`);
			}

			if (disposition?.length) {
        for (const d of disposition) {
          params.push(`disposition=${encodeURIComponent(d)}`);
        }
			}

			if (attemptsFrom) {
				params.push(`attemptsFrom=${attemptsFrom}`);
			}
    
			if (attemptsTo) {
				params.push(`attemptsTo=${attemptsTo}`);
			}

			const paramsString = params.join("&");

			// "Do not call" is not a lead status change. It adds the contact to the account-wide
			// do-not-call list, so it goes to its own endpoint - the PUT below would only have
			// marked this one campaign's lead as done, which is what the label always implied
			// but never did.
			if (action === LeadUpdateAction.DO_NOT_CALL_AGAIN) {
				const response = await apiClient.post(
					`/campaigns/${campaignId}/leads/suppress${paramsString ? `?${paramsString}` : ""}`,
					data
				);
				return response.data;
			}

			const response = await apiClient.put(
				`/campaigns/${campaignId}/leads${paramsString ? `?${paramsString}` : ""}`,
				data
			);
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignsKeys.leads(variables.campaignId) });
			queryClient.invalidateQueries({ queryKey: ["suppressions"] });
			queryClient.invalidateQueries({ queryKey: ["contacts"] });
			if (variables.id) {
				queryClient.invalidateQueries({ queryKey: campaignsKeys.lead(variables.campaignId, variables.id) });
			}

			const action = variables.action;

			switch (action) {
				case LeadUpdateAction.ADD_TO_QUEUE: {
					toast.success("Action: Added to queue completed successfully");

					break;
				}
				case LeadUpdateAction.SCHEDULE_CUSTOM_CALL_BACK: {
					toast.success("Action: Scheduled custom call back completed successfully");

					break;
				}
				case LeadUpdateAction.RESET_CALL_BACK_ATTEMPTS: {
					toast.success("Action: Reset call back attempts completed successfully");

					break;
				}
				case LeadUpdateAction.DO_NOT_CALL_AGAIN: {
					const count = (data as { suppressed?: number } | undefined)?.suppressed;
					toast.success(
						count === undefined
							? "Added to the do-not-call list"
							: `${count} contact${count === 1 ? "" : "s"} added to the do-not-call list`
					);

					break;
				}
				default: {
					toast.success("Success");
				}
			}
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Campaign Contact update failed");
		},
	});
}

export function useAnalyseCampaignLead() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			campaignId,
			status,
			disposition,
			data,
      attemptsFrom,
      attemptsTo,
		}: {
			campaignId: string;
			status?: string;
			disposition?: string[];
			data: any;
			id?: string;
			action: string;
			attemptsFrom?: string;
			attemptsTo?: string;
		}) => {
			const params: string[] = [];
			if (status) {
				params.push(`status=${status}`);
			}

			if (disposition?.length) {
        for (const d of disposition) {
          params.push(`disposition=${encodeURIComponent(d)}`);
        }
			}

			if (attemptsFrom) {
				params.push(`attemptsFrom=${attemptsFrom}`);
			}
    
			if (attemptsTo) {
				params.push(`attemptsTo=${attemptsTo}`);
			}

			const paramsString = params.join("&");
  
			const response = await apiClient.post(
				`/campaigns/${campaignId}/leads/analyse${paramsString ? `?${paramsString}` : ""}`,
				data
			);
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignsKeys.leads(variables.campaignId) });
			if (variables.id) {
				queryClient.invalidateQueries({ queryKey: campaignsKeys.lead(variables.campaignId, variables.id) });
			}
			toast.success("Action: Re-fetch Dispositions & Summary added to the queue");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Campaign Contact update failed");
		},
	});
}

export function useCopyCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ campaignId, data }: { campaignId: string; data: any }) => {
			const response = await apiClient.post(`/campaigns/${campaignId}/clone`, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
			toast.success("Campaign copied successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Campaign copy failed");
		},
	});
}